import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import adminAxios from '../adminAxios'
import { API } from '../api'
import { authClient } from '../authClient'
import '../styles/facilitador.css'

interface ParamFiltro { id: number; campo: string; valores: string[] }

export function FacilitadorPage() {
    const navigate = useNavigate()

    // ========== ACESSIBILIDADE ==========
    const [largeText, setLargeText] = useState(false)
    const [highContrast, setHighContrast] = useState(false)

    // ========== AUTH GATE ==========
    const [authenticated, setAuthenticated] = useState(false)
    const [authEmail, setAuthEmail] = useState('facilitador@capjota.local')
    const [authPassword, setAuthPassword] = useState('')
    const [authError, setAuthError] = useState('')

    useEffect(() => {
        if (sessionStorage.getItem('baToken')) setAuthenticated(true)
    }, [])

    const handleAuth = async () => {
        setAuthError('')
        const result = await authClient.signIn.email({
            email: authEmail,
            password: authPassword,
        })
        if (result.error) {
            setAuthError('Credenciais inválidas')
            return
        }
        const token = (result as { data?: { token?: string } }).data?.token
            ?? sessionStorage.getItem('baToken')
        if (token) sessionStorage.setItem('baToken', token)
        setAuthenticated(true)
    }

    const [listaTurmas, setListaTurmas] = useState<{ ID: number; nome: string }[]>([])
    const [listaUnidades, setListaUnidades] = useState<{ id: number; nome: string; nivel: number; superior: number; qtd: number; emAndamento: number; concluidos: number; stat: number }[]>([])
    const [listaArquivos, setListaArquivos] = useState<{ name: string; downloadUrl: string; filtros?: string | null }[]>([])
    const [total, setTotal] = useState(0)
    const [concluidos, setConcluidos] = useState(0)
    const [conclusao, setConclusao] = useState(0)
    const [turma, setTurma] = useState(0)
    const [nivelInicial, setNivelInicial] = useState('1')
    const [nivelFinal, setNivelFinal] = useState('5')
    const [processing, setProcessing] = useState(false)
    const [selectedRows, setSelectedRows] = useState(new Set<number>())
    const [processingTime, setProcessingTime] = useState(0)
    const [startTime, setStartTime] = useState<number | null>(null)

    // Parâmetros de filtro da turma
    const [parametrosFiltro, setParametrosFiltro] = useState<ParamFiltro[]>([])
    const [selectedFiltros, setSelectedFiltros] = useState<Record<number, Set<string>>>({})

    const buscarTurmas = useCallback(async () => {
        try {
            const res = await adminAxios.get(`${API.FACILITADOR}/turmas`)
            setListaTurmas(res.data)
        } catch (err) {
            console.error(err)
        }
    }, [])

    // Timer para processamento
    useEffect(() => {
        let interval: any
        if (processing && startTime) {
            interval = setInterval(() => {
                const elapsed = Math.floor((Date.now() - startTime) / 1000)
                setProcessingTime(elapsed)
            }, 100)
        } else {
            setProcessingTime(0)
            setStartTime(null)
        }
        return () => {
            if (interval) clearInterval(interval)
        }
    }, [processing, startTime])

    const consultaEvolucaoTurma = useCallback(async () => {
        if (turma === 0) return
        try {
            const res = await adminAxios.get(`${API.FACILITADOR}/evolucao-turma/${turma}`)
            setTotal(res.data.total)
            setConcluidos(res.data.concluidos)
            setConclusao(res.data.conclusao)
        } catch (err) {
            console.error(err)
        }
    }, [turma])

    const consultaEvolucaoHierarquia = useCallback(async () => {
        if (turma === 0) return
        try {
            const res = await adminAxios.get(`${API.FACILITADOR}/evolucao-hierarquia/${turma}`)
            setListaUnidades(res.data)
        } catch (err) {
            console.error(err)
        }
    }, [turma])

    const buscarArquivos = useCallback(async () => {
        if (turma === 0) return
        try {
            const res = await adminAxios.get(`${API.FACILITADOR}/arquivos/${turma}`)
            setListaArquivos(res.data)
        } catch (err) {
            console.error(err)
        }
    }, [turma])

    const buscarParametrosFiltro = useCallback(async () => {
        if (turma === 0) return
        try {
            const res = await adminAxios.get(`${API.FACILITADOR}/parametros-filtro/${turma}`)
            setParametrosFiltro(res.data)
            // Por padrão todos os valores de cada parâmetro ficam selecionados
            const defaults: Record<number, Set<string>> = {}
            for (const p of res.data as ParamFiltro[]) {
                defaults[p.id] = new Set(p.valores)
            }
            setSelectedFiltros(defaults)
        } catch (err) {
            console.error(err)
        }
    }, [turma])

    useEffect(() => {
        if (authenticated) buscarTurmas()
    }, [buscarTurmas, authenticated])

    useEffect(() => {
        if (authenticated && turma !== 0) {
            consultaEvolucaoTurma()
            consultaEvolucaoHierarquia()
            buscarArquivos()
            buscarParametrosFiltro()
        }
    }, [authenticated, turma, consultaEvolucaoTurma, consultaEvolucaoHierarquia, buscarArquivos, buscarParametrosFiltro])

    useEffect(() => {
        const interval = setInterval(() => {
            if (authenticated && turma !== 0) {
                consultaEvolucaoTurma()
                consultaEvolucaoHierarquia()
                buscarArquivos()
            }
        }, 5000)
        return () => clearInterval(interval)
    }, [authenticated, turma, consultaEvolucaoTurma, consultaEvolucaoHierarquia, buscarArquivos])

    const toggleRow = (id: number) => {
        const newSelected = new Set(selectedRows)
        if (newSelected.has(id)) {
            newSelected.delete(id)
        } else {
            newSelected.add(id)
        }
        setSelectedRows(newSelected)
    }

    const toggleSelectAll = () => {
        if (selectedRows.size === listaUnidades.length) {
            setSelectedRows(new Set())
        } else {
            setSelectedRows(new Set(listaUnidades.map(u => u.id)))
        }
    }

    const mudaTurma = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setTurma(parseInt(e.target.value) || 0)
    }

    const toggleFiltroValor = (parametroId: number, valor: string) => {
        setSelectedFiltros(prev => {
            const atual = new Set(prev[parametroId] ?? [])
            if (atual.has(valor)) { atual.delete(valor) } else { atual.add(valor) }
            return { ...prev, [parametroId]: atual }
        })
    }

    const toggleTodosFiltro = (parametroId: number, valores: string[]) => {
        setSelectedFiltros(prev => {
            const atual = prev[parametroId] ?? new Set()
            const todos = valores.every(v => atual.has(v))
            return { ...prev, [parametroId]: todos ? new Set() : new Set(valores) }
        })
    }

    const processar = async () => {
        if (!turma || turma === 0) {
            alert("Selecione uma turma antes de processar")
            return
        }
        if (selectedRows.size === 0) {
            alert("Selecione pelo menos uma unidade organizacional para processar o debriefing")
            return
        }

        setProcessing(true)
        setStartTime(Date.now())

        try {
            const elementos = Array.from(selectedRows)

            // Converter selectedFiltros (Record<id, Set<string>>) para objeto JSON serializável
            const filtrosParametros: Record<number, string[]> = {}
            for (const [id, vals] of Object.entries(selectedFiltros)) {
                const arr = [...(vals as Set<string>)]
                if (arr.length > 0) filtrosParametros[Number(id)] = arr
            }

            const response = await adminAxios.post(`${API.FACILITADOR}/processar-debriefing`, {
                turmaId: turma,
                unidadeIds: elementos,
                nivelInicial: nivelInicial,
                nivelFinal: nivelFinal,
                filtrosParametros
            }, {
                responseType: 'blob',
                timeout: 600000
            })

            // Extrair nome do arquivo do header Content-Disposition, se disponível
            const disposition = response.headers['content-disposition'] || ''
            const nameMatch = disposition.match(/filename="?([^";\r\n]+)"?/)
            const rawName = nameMatch ? nameMatch[1] : `Debriefing_Turma_${turma}.pptx`
            const fileName = rawName.replace(/[^a-zA-Z0-9._,()\-\s]/g, '_').trim() || `Debriefing_Turma_${turma}.pptx`

            const url = window.URL.createObjectURL(new Blob([response.data]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', fileName)
            document.body.appendChild(link)
            link.click()

            setTimeout(() => {
                if (link.parentNode) link.parentNode.removeChild(link)
                window.URL.revokeObjectURL(url)
                buscarArquivos()
            }, 150)

            alert('Debriefing gerado com sucesso!')
        } catch (err: any) {
            console.error(err)
            if (err.response?.data instanceof Blob) {
                const text = await err.response.data.text().catch(() => '')
                try {
                    const json = JSON.parse(text)
                    alert('Erro ao gerar debriefing: ' + (json.error || text))
                } catch {
                    alert('Erro ao gerar debriefing: ' + (text || err.message))
                }
            } else {
                alert('Erro ao gerar debriefing: ' + (err.message || 'Erro desconhecido'))
            }
        } finally {
            setProcessing(false)
        }
    }

    // ========== AUTH GATE RENDER ==========
    if (!authenticated) {
        return (
            <div className={`fac-page ${largeText ? 'a11y-large' : ''} ${highContrast ? 'a11y-contrast' : ''}`} style={{ justifyContent: 'center', alignItems: 'center', padding: 'clamp(16px, 4vw, 24px)', boxSizing: 'border-box' }}>
                <div className="fac-card" style={{ maxWidth: '420px', width: '100%', margin: '0 auto', textAlign: 'center', padding: 'clamp(20px, 5vw, 32px)', boxSizing: 'border-box' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
                        <div style={{ width: '56px', height: '56px', background: 'var(--fac-navy)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '20px', fontWeight: 800 }}>
                            BMI
                        </div>
                    </div>
                    <h2 style={{ color: 'var(--fac-navy)', fontSize: 'clamp(1.2rem, 5vw, 1.4rem)', fontWeight: 800, margin: '0 0 6px' }}>
                        Painel do Facilitador
                    </h2>
                    <p style={{ color: 'var(--fac-muted)', fontSize: '0.875rem', margin: '0 0 20px', lineHeight: '1.4' }}>
                        Autenticação requerida para acesso aos relatórios e debriefing
                    </p>
                    <input
                        type="email"
                        aria-label="E-mail do facilitador"
                        placeholder="E-mail"
                        value={authEmail}
                        onChange={(e) => setAuthEmail(e.target.value)}
                        style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1.5px solid var(--fac-line)',
                            borderRadius: '8px',
                            marginBottom: '14px',
                            fontSize: '16px',
                            minHeight: '48px',
                            boxSizing: 'border-box'
                        }}
                    />
                    <input
                        type="password"
                        aria-label="Senha do facilitador"
                        placeholder="Senha"
                        value={authPassword}
                        onChange={(e) => setAuthPassword(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleAuth()}
                        style={{
                            width: '100%',
                            padding: '12px 14px',
                            border: '1.5px solid var(--fac-line)',
                            borderRadius: '8px',
                            marginBottom: '20px',
                            fontSize: '16px',
                            minHeight: '48px',
                            boxSizing: 'border-box'
                        }}
                    />
                    {authError && (
                        <div role="alert" style={{ color: 'var(--fac-err)', background: 'var(--fac-err-soft)', padding: '10px 14px', borderRadius: '6px', marginBottom: '16px', fontSize: '0.875rem', fontWeight: 600, border: '1px solid rgba(153, 27, 27, 0.2)' }}>
                            {authError}
                        </div>
                    )}
                    <button
                        onClick={handleAuth}
                        className="fac-btn-process"
                        style={{ width: '100%', minHeight: '48px', margin: 0 }}
                    >
                        Entrar no Painel
                    </button>
                    <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center', gap: '8px' }}>
                        <button
                            type="button"
                            onClick={() => setLargeText(!largeText)}
                            className={`fac-a11y-btn ${largeText ? 'active' : ''}`}
                            style={{ color: 'var(--fac-ink)', border: '1px solid var(--fac-line)', background: '#fff', minHeight: '44px' }}
                        >
                            A+ Texto
                        </button>
                        <button
                            type="button"
                            onClick={() => setHighContrast(!highContrast)}
                            className={`fac-a11y-btn ${highContrast ? 'active' : ''}`}
                            style={{ color: 'var(--fac-ink)', border: '1px solid var(--fac-line)', background: '#fff', minHeight: '44px' }}
                        >
                            🌓 Contraste
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className={`fac-page ${largeText ? 'a11y-large' : ''} ${highContrast ? 'a11y-contrast' : ''}`}>
            {/* Header com Design System Cap Jota e Toolbar de Acessibilidade */}
            <header className="fac-header">
                <div className="fac-header-row">
                    <div className="fac-brand-group">
                        <span className="fac-logo">cap jota</span>
                        <span className="fac-sublogo">| Painel do Facilitador</span>
                    </div>

                    <div className="fac-a11y-tools">
                        <button
                            type="button"
                            onClick={() => setLargeText(!largeText)}
                            className={`fac-a11y-btn ${largeText ? 'active' : ''}`}
                            title="Alternar tamanho ampliado de texto"
                            aria-pressed={largeText}
                        >
                            A+ <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>Texto</span>
                        </button>
                        <button
                            type="button"
                            onClick={() => setHighContrast(!highContrast)}
                            className={`fac-a11y-btn ${highContrast ? 'active' : ''}`}
                            title="Alternar modo de alto contraste"
                            aria-pressed={highContrast}
                        >
                            🌓 <span style={{ fontSize: '0.75rem', opacity: 0.9 }}>Contraste</span>
                        </button>
                        <button
                            onClick={() => navigate('/menu')}
                            className="fac-btn-voltar"
                        >
                            ← Menu Principal
                        </button>
                    </div>
                </div>
            </header>

            <main className="fac-main">
                {/* Seleção de Turma */}
                <section className="fac-card">
                    <div className="fac-card-header">
                        <h2 className="fac-card-title">
                            🏢 Selecionar Turma
                        </h2>
                        {turma > 0 && (
                            <span className="fac-pill fac-pill-ok">Turma #{turma}</span>
                        )}
                    </div>
                    <div className="fac-select-group">
                        <label htmlFor="select-turma-fac" className="fac-select-label">
                            Escolha a turma para acompanhar o progresso e gerar o relatório:
                        </label>
                        <select
                            id="select-turma-fac"
                            value={turma}
                            onChange={mudaTurma}
                            className="fac-select"
                        >
                            <option value="0">-- Selecione uma turma --</option>
                            {listaTurmas.map(t => (
                                <option key={t.ID} value={t.ID}>
                                    (Código: {t.ID}) {t.nome}
                                </option>
                            ))}
                        </select>
                    </div>
                </section>

                {/* Métricas e Acompanhamento */}
                <section className="fac-kpi-grid">
                    <div className="fac-kpi-card">
                        <span className="fac-kpi-label">Progresso Geral</span>
                        <div className="fac-kpi-val ok">{conclusao}%</div>
                        <span className="fac-kpi-sub">taxa de conclusão da turma</span>
                    </div>
                    <div className="fac-kpi-card">
                        <span className="fac-kpi-label">Iniciados</span>
                        <div className="fac-kpi-val">{total}</div>
                        <span className="fac-kpi-sub">participantes ativos</span>
                    </div>
                    <div className="fac-kpi-card">
                        <span className="fac-kpi-label">Concluídos</span>
                        <div className="fac-kpi-val ok">{concluidos}</div>
                        <span className="fac-kpi-sub">questionários finalizados</span>
                    </div>
                </section>

                {/* Tabela de Unidades Organizacionais */}
                <section className="fac-card">
                    <div className="fac-card-header">
                        <h2 className="fac-card-title">
                            📊 Unidades Organizacionais para Debriefing
                        </h2>
                        <span style={{ fontSize: '0.85rem', color: 'var(--fac-muted)' }}>
                            {selectedRows.size} de {listaUnidades.length} selecionada(s)
                        </span>
                    </div>

                    <p style={{ fontSize: '0.875rem', color: 'var(--fac-muted)', marginBottom: '14px' }}>
                        Selecione as unidades que farão parte do relatório de debriefing a ser gerado:
                    </p>

                    <div className="fac-table-wrapper table-responsive">
                        <table className="fac-table">
                            <thead>
                                <tr>
                                    <th style={{ width: '44px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            aria-label="Selecionar todas as unidades"
                                            onChange={toggleSelectAll}
                                            checked={listaUnidades.length > 0 && selectedRows.size === listaUnidades.length}
                                            style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                        />
                                    </th>
                                    <th style={{ textAlign: 'center' }}>ID</th>
                                    <th>Unidade</th>
                                    <th style={{ textAlign: 'center' }}>Nível</th>
                                    <th style={{ textAlign: 'center' }}>Superior</th>
                                    <th style={{ textAlign: 'center' }}>Qtd. Total</th>
                                    <th style={{ textAlign: 'center' }} title="Começaram a responder e ainda não finalizaram">Em andamento</th>
                                    <th style={{ textAlign: 'center' }}>Concluídos</th>
                                    <th style={{ textAlign: 'center' }}>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {listaUnidades.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} style={{ padding: '28px', textAlign: 'center', color: 'var(--fac-muted)', fontStyle: 'italic' }}>
                                            {turma === 0 ? 'Selecione uma turma para visualizar as unidades.' : 'Nenhuma unidade encontrada para esta turma.'}
                                        </td>
                                    </tr>
                                ) : (
                                    listaUnidades.map(u => {
                                        const isChecked = selectedRows.has(u.id)
                                        return (
                                            <tr
                                                key={u.id}
                                                onClick={() => toggleRow(u.id)}
                                                className={isChecked ? 'selected' : ''}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                <td style={{ textAlign: 'center' }}>
                                                    <input
                                                        type="checkbox"
                                                        aria-label={`Selecionar unidade ${u.nome}`}
                                                        checked={isChecked}
                                                        onChange={() => toggleRow(u.id)}
                                                        onClick={(e) => e.stopPropagation()}
                                                        style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                                                    />
                                                </td>
                                                <td style={{ textAlign: 'center', fontWeight: 600 }}>{u.id}</td>
                                                <td style={{ fontWeight: 600 }}>{u.nome}</td>
                                                <td style={{ textAlign: 'center' }}>{u.nivel}</td>
                                                <td style={{ textAlign: 'center' }}>{u.superior}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700 }}>{u.qtd}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700 }}>{u.emAndamento ?? 0}</td>
                                                <td style={{ textAlign: 'center', fontWeight: 700 }}>{u.concluidos}</td>
                                                <td style={{ textAlign: 'center' }}>
                                                    <span className={`fac-pill ${u.stat >= 80 ? 'fac-pill-ok' : u.stat >= 50 ? 'fac-pill-warn' : 'fac-pill-err'}`}>
                                                        {u.stat}%
                                                    </span>
                                                </td>
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Faixa de Níveis */}
                    <div className="fac-niveis-grid">
                        <div className="fac-nivel-item">
                            <label htmlFor="nivel-inicial-input" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                Nível Inicial:
                            </label>
                            <input
                                id="nivel-inicial-input"
                                type="text"
                                value={nivelInicial}
                                onChange={(e) => setNivelInicial(e.target.value)}
                                className="fac-nivel-input"
                            />
                        </div>
                        <div className="fac-nivel-item">
                            <label htmlFor="nivel-final-input" style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                                Nível Final:
                            </label>
                            <input
                                id="nivel-final-input"
                                type="text"
                                value={nivelFinal}
                                onChange={(e) => setNivelFinal(e.target.value)}
                                className="fac-nivel-input"
                            />
                        </div>
                    </div>
                </section>

                {/* Filtros de Parâmetros */}
                {parametrosFiltro.length > 0 && (
                    <section className="fac-card">
                        <div className="fac-card-header">
                            <h2 className="fac-card-title">
                                🔍 Filtros Demográficos / Parâmetros
                            </h2>
                        </div>
                        <p style={{ fontSize: '0.85rem', color: 'var(--fac-muted)', marginBottom: '8px' }}>
                            Refine o público que entrará na geração do relatório:
                        </p>
                        <div className="fac-filtros-grid">
                            {parametrosFiltro.map(param => {
                                const sel = selectedFiltros[param.id] ?? new Set()
                                const todos = param.valores.length > 0 && param.valores.every(v => sel.has(v))
                                return (
                                    <div key={param.id} className="fac-filtro-box">
                                        <div className="fac-filtro-header">
                                            <span>{param.campo}</span>
                                            {sel.size > 0 && (
                                                <span className="fac-filtro-badge">{sel.size}</span>
                                            )}
                                        </div>
                                        {param.valores.length === 0 ? (
                                            <div style={{ fontSize: '0.8rem', color: 'var(--fac-muted)', fontStyle: 'italic' }}>
                                                Sem valores cadastrados
                                            </div>
                                        ) : (
                                            <>
                                                <label className="fac-check-label" style={{ marginBottom: '8px', paddingBottom: '6px', borderBottom: '1px solid var(--fac-line)' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={todos}
                                                        onChange={() => toggleTodosFiltro(param.id, param.valores)}
                                                    />
                                                    <strong>(Todos)</strong>
                                                </label>
                                                <div className="fac-filtro-list">
                                                    {param.valores.map(v => (
                                                        <label key={v} className="fac-check-label">
                                                            <input
                                                                type="checkbox"
                                                                checked={sel.has(v)}
                                                                onChange={() => toggleFiltroValor(param.id, v)}
                                                            />
                                                            <span>{v}</span>
                                                        </label>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                )
                            })}
                        </div>
                    </section>
                )}

                {/* Botão de Processar */}
                <div className="fac-action-row">
                    <button
                        onClick={processar}
                        disabled={processing || turma === 0 || selectedRows.size === 0}
                        className="fac-btn-process"
                    >
                        {processing ? '⏳ Processando Relatório...' : `▶ Gerar Relatório Debriefing (${selectedRows.size} unid.)`}
                    </button>
                </div>

                {/* Modal de Processamento */}
                {processing && (
                    <div className="fac-modal-overlay" role="dialog" aria-modal="true" aria-labelledby="modal-title-proc">
                        <div className="fac-modal-content">
                            <h3 id="modal-title-proc" style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--fac-navy)' }}>
                                🔄 Processando Debriefing
                            </h3>
                            <div className="fac-timer-digits">
                                {Math.floor(processingTime / 60).toString().padStart(2, '0')}:
                                {(processingTime % 60).toString().padStart(2, '0')}
                            </div>
                            <p style={{ fontSize: '0.85rem', color: 'var(--fac-muted)', marginBottom: '16px' }}>
                                minutos : segundos decorridos
                            </p>
                            <p style={{ fontSize: '0.9rem', color: 'var(--fac-ink)', fontWeight: 600 }}>
                                Gerando apresentação consolidada para {selectedRows.size} unidade(s)...
                            </p>
                        </div>
                    </div>
                )}

                {/* Arquivos Gerados */}
                <section className="fac-card">
                    <div className="fac-card-header">
                        <h2 className="fac-card-title">
                            📁 Arquivos Gerados ({listaArquivos.length})
                        </h2>
                    </div>
                    {listaArquivos.length === 0 ? (
                        <p style={{ color: 'var(--fac-muted)', fontStyle: 'italic', margin: '8px 0' }}>
                            Nenhum arquivo gerado ainda para esta turma.
                        </p>
                    ) : (
                        <div className="fac-files-list">
                            {listaArquivos.map((arq, idx) => (
                                <div key={idx} className="fac-file-item">
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', minWidth: 0 }}>
                                    <a
                                        href="#"
                                        className="fac-file-name"
                                        onClick={async (e) => {
                                            e.preventDefault()
                                            try {
                                                const resp = await adminAxios.get(`${API.BASE}${arq.downloadUrl}`, { responseType: 'blob' })
                                                const url = window.URL.createObjectURL(new Blob([resp.data]))
                                                const link = document.createElement('a')
                                                link.href = url
                                                link.setAttribute('download', arq.name)
                                                document.body.appendChild(link)
                                                link.click()
                                                setTimeout(() => { link.parentNode?.removeChild(link); window.URL.revokeObjectURL(url) }, 150)
                                            } catch {
                                                alert('Erro ao baixar arquivo')
                                            }
                                        }}
                                    >
                                        📊 {arq.name}
                                    </a>
                                    <span className="fac-file-filtros" style={{ fontSize: '0.8rem', color: 'var(--fac-muted)', paddingLeft: '1.6em' }}>
                                        Filtros: {arq.filtros ?? 'não registrados'}
                                    </span>
                                    </div>
                                    <span className="fac-pill fac-pill-ok">Disponível para download</span>
                                </div>
                            ))}
                        </div>
                    )}
                </section>
            </main>
        </div>
    )
}
